import React, { useState } from 'react';
import { FlowNode } from '../../../../types/flowBuilder';
import { BaseConfigProps, ConfigSection, ConfigField } from '../shared/ConfigFormHelpers';

interface FormConfigProps extends BaseConfigProps {
  node: FlowNode;
  updateConfig: (key: string, value: any) => void;
}

const FormConfig: React.FC<FormConfigProps> = ({ node, updateConfig }) => {
  const [fields, setFields] = useState<Array<{name: string, type: string, label: string, required: boolean}>>(
    node.data.config?.fields || []
  );

  const addField = () => {
    const newFields = [...fields, { name: '', type: 'text', label: '', required: false }];
    setFields(newFields);
    updateConfig('fields', newFields);
  };

  const updateField = (index: number, key: string, value: any) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [key]: value };
    setFields(newFields);
    updateConfig('fields', newFields);
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    updateConfig('fields', newFields);
  };

  return (
    <div className="space-y-4">
      <ConfigSection title="Űrlap beállítások">
        <ConfigField
          label="Űrlap címe"
          description="Az űrlap fejléce"
        >
          <input
            type="text"
            value={node.data.config?.title || ''}
            onChange={(e) => updateConfig('title', e.target.value)}
            placeholder="Adatok megadása"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>

        <ConfigField
          label="Leírás"
          description="Opcionális leírás"
        >
          <textarea
            value={node.data.config?.description || ''}
            onChange={(e) => updateConfig('description', e.target.value)}
            placeholder="Kérem töltse ki az alábbi mezőket..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
        </ConfigField>
      </ConfigSection>

      <ConfigSection title="Űrlap mezők">
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Mező #{index + 1}</span>
                <button
                  onClick={() => removeField(index)}
                  className="text-red-600 hover:text-red-700 text-sm"
                >
                  Törlés
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={field.name}
                  onChange={(e) => updateField(index, 'name', e.target.value)}
                  placeholder="Mező neve"
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(index, 'label', e.target.value)}
                  placeholder="Címke"
                  className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex gap-2 items-center">
                <select
                  value={field.type}
                  onChange={(e) => updateField(index, 'type', e.target.value)}
                  className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="text">Szöveg</option>
                  <option value="email">Email</option>
                  <option value="number">Szám</option>
                  <option value="date">Dátum</option>
                  <option value="textarea">Hosszú szöveg</option>
                  <option value="select">Legördülő</option>
                  <option value="checkbox">Jelölőnégyzet</option>
                </select>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={(e) => updateField(index, 'required', e.target.checked)}
                    className="mr-1"
                  />
                  Kötelező
                </label>
              </div>
            </div>
          ))}
          <button
            onClick={addField}
            className="w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
          >
            + Mező hozzáadása
          </button>
        </div>
      </ConfigSection>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
        <div className="text-xs text-blue-700 dark:text-blue-300">
          <strong>📝 Dinamikus űrlap:</strong>
          <br />• Felhasználói adatbevitel
          <br />• Validáció támogatás
          <br />• Eredmény: <code>{'{{formData}}'}</code>
        </div>
      </div>
    </div>
  );
};

export default FormConfig;
